import {POPUP} from "back/commons/widgets/popups";
import "back/wsp/views/itemMain"; //on force l'import des tags ItemMain.
import {IInfo, InfoBrokerBasic} from "lib/commons/infos";
import {IReg, REG} from 'lib/commons/registry';
import {VIEWS} from "lib/commons/views";
import {JSX} from "lib/commons/xml/dom";
import {InfoCurrentItem, InfoFocusItem, InfoFocusNodeInItem, InfoReqCurrentItem, InfoSrcUriMoved, ITEM} from "lib/wsp/item";
import {JSrcFields, SRC, srcRef} from "lib/wsp/src";
import {IWspUiEnv, JWspUriChangeMsg} from "lib/wsp/wsp";
import {EWspChangesEvts, WspsLivePlace} from "lib/wsp/wspsLive";
import {JLastDatas} from "lib/commons/lastDatas";
import {IItemViewer, ISrcMainCtx} from "back/wsp/views/itemMain";

interface ItemViewerSingleConf {
	/** Désactive les possibilités de chagement automatique de l'item courant. ex :event InfoFocusItem de l'infoBroker, ... */
	disableAutoChangeCurrentItem?: boolean

	mainViewCode?: string
}


/**
 * Gestionnaire de visualisation des items.
 * Implémentation triviale, un seul item à la fois, pas d'historique.
 */
export class ItemViewerSingle implements IItemViewer {
	conf: ItemViewerSingleConf;

	reg: IReg<IWspUiEnv>;

	mainViewParent: HTMLElement;

	/** Item en cours. */
	longDesc: JSrcFields;

	fallbackOpenSrcRef?: (srcRef: srcRef, longdesc: JSrcFields, silent?: boolean) => Promise<true | null>;

	protected onWspUriChange: (msg: JWspUriChangeMsg, from: WspsLivePlace | 'local' | 'server') => void;

	protected lastDatas: JLastDatas;

	constructor(conf?: ItemViewerSingleConf) {
		this.conf = conf || {};
	}

	initViewer(reg: IReg<IWspUiEnv>, mainViewParent: HTMLElement, lastDatas: JLastDatas, select: srcRef): Promise<any> {
		if (this.reg) console.error("ItemViewerSingle already inited");
		this.reg = reg;
		this.mainViewParent = mainViewParent;
		const infoBroker = reg.env.infoBroker;
		if (infoBroker) infoBroker.addConsumer(this);
		this.lastDatas = lastDatas;

		this.onWspUriChange = this._onWspUriChange.bind(this);
		reg.env.place.eventsMgr.on("wspUriChange", this.onWspUriChange);

		if (select) return this.openSrcRef(select);
	}

	onInfo(info: IInfo): void {
		if (!this.conf.disableAutoChangeCurrentItem && info instanceof InfoFocusItem) {
			if (info.handled) return;
			if (info instanceof InfoFocusNodeInItem) {
				this.focusNodeInItem(info);
			} else {
				this.focusItem(info);
			}
			info.handled = true;
		} else if (info instanceof InfoReqCurrentItem) {
			info.srcUri = this.longDesc ? this.longDesc.srcUri : null;
			info.shortDesc = this.longDesc;
		} else if (info instanceof InfoSrcUriMoved) {
			this.onSrcMoved(info);
		}
	}

	focusItem(info: InfoFocusItem) {
		this.openSrcRef(info.srcRef);
	}

	async focusNodeInItem(info: InfoFocusNodeInItem) {
		if (this.longDesc && info.srcRef === SRC.srcRef(this.longDesc)) return; //item déjà chargé, il va gérer la partie "inItem".
		if (await this.openSrcRef(info.srcRef)) {
			//On redispatch la demande une fois l'item chargé.
			this.reg.env.infoBroker.dispatchInfo(new InfoFocusNodeInItem(info.xpath, info.srcRef), this);
		}
	}

	/**
	 * Si l'ancienne uri est présente et la nouvelle uri est absente,
	 * on remplace l'onglet avec la nouvelle uri.
	 */
	onSrcMoved(info: InfoSrcUriMoved) {
		if (this.longDesc.srcUri === info.oldSrcUri) this.openSrcRef(info.newSrcUri);
	}

	/**
	 * @return true: ouvert, false: abandon user, null: srcRef pas trouvé.
	 */
	async openSrcRef(srcRef: srcRef, silent?: boolean): Promise<boolean | null> {
		if (!srcRef) return this.tryCloseMainView(true);
		if (this.longDesc && srcRef === SRC.srcRef(this.longDesc)) return true;
		const wsp = this.reg.env.wsp;
		const longDesc = this.longDesc = await wsp.fetchLongDesc(srcRef);
		if (longDesc.srcSt < 0 || longDesc.itModel == null) {
			if (this.fallbackOpenSrcRef) {
				return this.fallbackOpenSrcRef(srcRef, longDesc, silent);
			} else {
				if (!silent) POPUP.showNotifInfo("Ce contenu n'est plus disponible.", this.mainViewParent);
				return null;
			}
		}
		return this.openSrc(longDesc);
	}

	async openSrc(longDesc: JSrcFields): Promise<boolean | null> {
		let count = 0;
		while (this.mainView) {//while pour race cond si 2ème openSrc au cours du await this.tryCloseMainView()
			const v = await this.tryCloseMainView(false);
			if (++count > 1) console.warn("ItemViewerSingle.openSrc() called twice", v);
			if (v === false) return false;
		}
		const wsp = this.reg.env.wsp;
		const itemType = wsp.wspMetaUi.getItemType(longDesc.itModel);
		const reg = REG.createSubRegMixed(this.reg, itemType.reg);
		if (!reg.env.infoBroker) reg.env.infoBroker = new InfoBrokerBasic(); //pour communication interne à l'item (tasks...)

		const ctx: ISrcMainCtx = {
			reg: reg,
			shortDesc: longDesc,
			longDesc: longDesc,
			mainViewCode: this.conf.mainViewCode
		};
		const view = itemType.getSrcMainArea(ctx).buildBody(ctx, this.lastDatas);
		if (this.lastDatas) this.lastDatas = null; // suppr lastDatas au 2ème appel.

		this.mainViewParent.appendChild(view);
		await view.initializedAsync;

		const infoBroker = reg.env.infoBroker;
		if (infoBroker) infoBroker.dispatchInfo(new InfoCurrentItem(longDesc.srcUri, longDesc), this);
		return true;
	}

	/**
	 * @return true: clos, false: annulation par le user, null: pas de mainView.
	 */
	async tryCloseMainView(dispatchInfo: boolean): Promise<boolean | null> {
		const mainView = this.mainView;
		if (!mainView) return null;
		if (await VIEWS.canHideView(mainView, true)) {
			this.closeMainView(dispatchInfo);
			return true;
		}
		return false;
	}

	private closeMainView(dispatchInfo: boolean) {
		this.longDesc = null;
		const mainView = this.mainView;
		if (mainView) {
			this.mainViewParent.textContent = null;
			VIEWS.onViewHidden(mainView, true);
			if (dispatchInfo && this.reg.env.infoBroker) this.reg.env.infoBroker.dispatchInfo(new InfoCurrentItem(null), this)
		}
	}

	get mainView(): HTMLElement {return this.mainViewParent.firstElementChild as HTMLElement}

	onViewShown() {
		//this.app.reg.env.wsp.
		VIEWS.onViewShown(this.mainView);
	}

	onViewHidden(closed?: boolean) {
		if (closed) {
			this.reg.env.place.eventsMgr.removeListener("wspUriChange", this.onWspUriChange);
			this.closeMainView(false /* fermeture globale, inutile de multiplier les events ?*/);
			const infoBroker = this.reg.env.infoBroker;
			if (infoBroker) infoBroker.removeConsumer(this);
		} else {
			VIEWS.onViewHidden(this.mainView, closed);
		}
	}

	// buildLastDatas(parentLastDatas: JLastDatas): void {
	// 	LASTDATAS.buildLastDatas(parentLastDatas, this.mainView, true);
	// }

	protected _onWspUriChange(msg: JWspUriChangeMsg, from: WspsLivePlace | 'local' | 'server') {
		if (this.longDesc) {
			const msgSrcRef = SRC.srcRef(msg);
			if (msg.type === EWspChangesEvts.r) {
				//Traitement du remove
				if (SRC.srcRef(this.longDesc) === msgSrcRef) {
					//Suppr de l'item
					this.closeMainView(true);
				} else if (ITEM.getSrcUriType(msg.srcUri) === 'space' && SRC.isSubUri(msg.srcUri, this.longDesc.srcUri)) {
					//Suppr d'un espace ancêtre de l'item
					this.closeMainView(true);
				}
			} else if (msg.type === EWspChangesEvts.u && SRC.srcRef(this.longDesc) === msgSrcRef) {
				//Traitement de l'update
				//item construit, faut détecter si l'itModel n'a pas été modifié
				const oldItModel = this.longDesc.itModel;
				this.reg.env.wsp.fetchLongDesc(msgSrcRef, this.mainViewParent).then((ld: JSrcFields) => {
					//cas du changement d'itModel, nécessité de le recharger.
					if (oldItModel !== ld.itModel) {
						if (oldItModel !== this.longDesc.itModel || SRC.srcRef(this.longDesc) === msgSrcRef) return;//race cond.
						this.openSrc(ld);
					}
				});
			}
		}
	}
}
